import React, { useEffect, useMemo, useState } from "react";
import DeclarationsInteractive from "./DeclarationsInteractive";
import Account_Details from "./account/My_Account"; 
import { isNotBlank } from "../utilities/InLine_validations"; 
import { useSecurity } from "./security/Security_store"; 

// LocalStorage keys
const STORAGE_DECL_PROGRESS = "members_declarations_progress_v1";
const STORAGE_THEIR_DOCS = "theirDocs";
const STORAGE_PAYMENT_STATUS = "payment.status.v1"; // { hasMethod: boolean, lastUpdated: iso, provider?: 'card'|'paypal'|'crypto' }

const TwoColRow = ({ left, right }) => (
  <div className="row g-2 mb-3 align-items-start">
    <div className="col-12 col-md-6">{left}</div>
    <div className="col-12 col-md-6">{right}</div>
  </div>
);

// Simple placeholder dashboard – consumes tokens
const Dashboard = () => {
  const { userName, keyId, sessionToken } = useSecurity();
  const payment = (() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_PAYMENT_STATUS) || "{}"); } catch { return {}; }
  })();

  return (
    <div className="container-fluid py-3">
      <h1 className="h4 mb-3">Members Dashboard</h1>
      <div className="border rounded p-3">
        <TwoColRow left={<strong>User</strong>} right={<span>{userName || "(unknown)"}</span>} />
        <TwoColRow left={<strong>Key ID</strong>} right={<code className="user-select-all">{keyId || "(none)"}</code>} />
        <TwoColRow left={<strong>Session Token</strong>} right={<code className="user-select-all">{sessionToken || "(none)"}</code>} />
        <TwoColRow left={<strong>Payment Status</strong>} right={<span>{payment?.hasMethod ? "On File" : "Missing"}</span>} />
      </div>
    </div>
  );
};

// Read-only Attestation status view
const AttestationSummary = ({ onContinue }) => {
  const finalDoc = useMemo(() => {
    try {
      const docs = JSON.parse(localStorage.getItem(STORAGE_THEIR_DOCS) || "[]");
      const latest = [...docs].reverse().find(d => d?.kind === "MemberDeclarations");
      if (latest) return latest;
    } catch {}
    try {
      const progress = JSON.parse(localStorage.getItem(STORAGE_DECL_PROGRESS) || "[]");
      const items = Array.isArray(progress) ? progress : [];
      const allDone = items.length > 0 && items.every(i => i.confirmed);
      return {
        kind: "MemberDeclarations",
        version: 1,
        userName: "(unknown)",
        issuedKeyId: "(unknown)",
        completedAt: allDone ? new Date().toISOString() : null,
        items: items.map((i, idx) => ({
          code: i.code || `DCL-${String(idx + 1).padStart(2, "0")}`,
          title: "(see declarations)",
          text: "(see declarations)",
          confirmed: !!i.confirmed,
          confirmedAt: i.confirmedAt || null
        }))
      };
    } catch { return null; }
  }, []);

  const done = finalDoc?.items?.filter(i => i.confirmed).length || 0;
  const total = finalDoc?.items?.length || 0;

  return (
    <div className="container-fluid py-3">
      <h1 className="h4 mb-3">Attestation Status</h1>
      <div className="alert alert-success d-flex justify-content-between align-items-center">
        <div>
          <strong>{done}/{total}</strong> attestations confirmed.
        </div>
        <button className="btn btn-primary" onClick={onContinue}>
          Membership Area →
        </button>
      </div>

      <div className="border rounded p-3">
        {finalDoc?.items?.map(it => (
          <div className="row g-2 mb-3" key={`${it.code}-${it.confirmedAt || ""}`}>
            <div className="col-12 col-md-6">
              <div><strong>{it.code}</strong></div>
            </div>
            <div className="col-12 col-md-6">
              <div className="d-flex align-items-center gap-2">
                <span className={`badge text-bg-${it.confirmed ? "success" : "secondary"}`}>
                  {it.confirmed ? "CONFIRMED" : "PENDING"}
                </span>
                {it.confirmedAt && <small className="text-muted">{new Date(it.confirmedAt).toLocaleString()}</small>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const hasCompletedAttestations = () => {
  try {
    const progress = JSON.parse(localStorage.getItem(STORAGE_DECL_PROGRESS) || "[]");
    if (!Array.isArray(progress) || progress.length === 0) return false;
    return progress.every(p => p.confirmed);
  } catch { return false; }
};

const hasPaymentOnFile = () => {
  try {
    const pay = JSON.parse(localStorage.getItem(STORAGE_PAYMENT_STATUS) || "{}");
    return !!pay?.hasMethod;
  } catch { return false; }
};

const MembersArea = () => {
  const { userName, keyId } = useSecurity();

  // use the validator to avoid unused import warning (and to ensure KeyID is usable)
  const hasKeyId = isNotBlank(keyId || "");

  const [stage, setStage] = useState(() => {
    // If no keyId (test/later Cognito), still allow flow but ensure attestation check
    if (!hasCompletedAttestations()) return "attest"; // 'attest' | 'attest_summary' | 'payment' | 'dashboard'
    return "attest_summary";
  });

  const handleAttestComplete = () => setStage("attest_summary");

  const goMembershipArea = () => {
    if (hasPaymentOnFile()) setStage("dashboard");
    else setStage("payment");
  };

  const handleMockPaymentCompleted = (provider = "card") => {
    localStorage.setItem(
      STORAGE_PAYMENT_STATUS,
      JSON.stringify({ hasMethod: true, provider, lastUpdated: new Date().toISOString() })
    );
    setStage("dashboard");
  };

  if (stage === "attest") {
    return (
      <DeclarationsInteractive
        userName={userName || "Member"}
        issuedKeyId={hasKeyId ? keyId : ""} // blank if not set; user will paste it
        onComplete={handleAttestComplete}
      />
    );
  }

  if (stage === "attest_summary") {
    return <AttestationSummary onContinue={goMembershipArea} />;
  }

  if (stage === "payment") {
    return <AccountWrapper onMockPaid={handleMockPaymentCompleted} />;
  }

  return <Dashboard />;
};

// Intercepts the mock payment “OK” from My_Account without altering its props.
const AccountWrapper = ({ onMockPaid }) => {
  useEffect(() => {
    window.__MOCK__PAYMENT_OK = (provider) => onMockPaid(provider);
    return () => { delete window.__MOCK__PAYMENT_OK; };
  }, [onMockPaid]);

  return <Account_Details />;
};

export default MembersArea;